export const useGuestStore = defineStore('guest', {
  state: () => {
    return {
      guest: null,

      confirmationOptions: [
        {
          id: 1,
          icon: 'custom/badge-check-circle',
          text: useT('event.analytics.guestConfirmed'),
        },
        {
          id: -1,
          icon: 'custom/badge-cancel-circle',
          text: useT('event.analytics.rejected'),
        },
        {
          id: 0,
          icon: 'custom/badge-help',
          text: useT('event.analytics.noResponse'),
        },
      ],

      attendanceOptions: [
        {
          id: 1,
          icon: 'custom/badge-check-circle',
          text: useT('event.analytics.attended'),
        },
        {
          id: 0,
          icon: 'custom/badge-cancel-circle',
          text: useT('event.analytics.didNotAttend'),
        },
      ],
    };
  },

  actions: {
    async fetchGuest({ _id }) {
      const response = await useApi.get(`/guests/${_id}`, {
        params: {
          event: useEventStore().event._id,
        },
      }).catch(() => {
        useToast.error(useT('common.errorLoadingData'));
      });

      this.guest = response || null;

      return response;
    },

    async updateGuest({ guestId, body }) {
      const guest = await useApi.patch(`/guests/${guestId}`, {
        body,
        params: {
          event: useEventStore().event._id,
        },
      }).catch((error) => {
        if (error?.statusCode === 400 && error?.response?._data) {
          useToast.error(error.response._data);
        } else {
          useToast.error(useT('common.errorSavingChanges'));
        }
      });

      if (guest) {
        this.guest = guest;
        return guest;
      }
    },

    async updateStarred({ guestId, starred }) {
      const guest = await useApi.patch(`/guests/${guestId}`, {
        body: { starred },
        params: {
          event: useEventStore().event._id,
        },
      }).catch(() => {
        useToast.error(useT('common.errorSavingChanges'));
      });

      if (guest) {
        if (guest.starred) {
          useToast(useT('guest.starWasSet'));
        } else {
          useToast(useT('guest.starWasRemoved'));
        }

        this.guest = {
          ...this.guest,
          starred: guest.starred,
        };
      }

      return guest;
    },

    async removeRoommates({ guestId }) {
      const response = await useApi.delete(`/guests/${guestId}/roommates`, {
        params: {
          event: useEventStore().event._id,
        },
      }).catch(() => {
        useToast.error(useT('common.errorSavingChanges'));
      });

      if (response) {
        this.guest = response;
        return response;
      }
    },

    async fetchGuestLogs({ guestId, mailing }) {
      const response = await useApi.get(`/guests/${guestId}/logs`, {
        params: {
          event: useEventStore().event._id,
          mailing,
        },
      }).catch(() => {
        useToast.error(useT('common.errorLoadingData'));
        return [];
      });

      return response;
    },

    async deleteGuest(id) {
      const response = await useApi.delete(`/guests/${id}`, {
        params: {
          event: useEventStore().event._id,
        },
      }).catch(() => {
        useToast.error(useT('guest.deleted.error'));
      });

      return response;
    },
  },

  getters: {
    getAttendanceOptions(state) {
      return _cloneDeep(state.attendanceOptions).map((option) => {
        if (useEventStore().isBeforeEndDate && option.id === 0) {
          option.icon = 'custom/badge-help';
          option.text = useT('guests.notYetAttended');
        }

        return option;
      });
    },

    getRoommateOptions() {
      return ({
        availableRoommates,
        form,
        roommatePropertiesIds,
      }) => {
        return (availableRoommates || [])
          .filter((roommate) => {
            if (roommate._id === form?._id) { // exclude current guest
              return false;
            }

            return true;
          })
          .map((roommate) => {
            const alreadySelected = Boolean(roommate.extras?.roommates?.length) || form?.extras?.roommates?.includes(roommate._id);

            // Check if roommate has compatible property options
            let hasCompatibleProperties = true;
            if (roommatePropertiesIds?.length) {
              hasCompatibleProperties = _uniq(roommatePropertiesIds).every((propertyId) => {
                if (!roommate.properties?.[propertyId]) { // roommate has no option
                  return true;
                }

                if (form?.properties?.[propertyId] === roommate.properties?.[propertyId]) { // guest has same option as roommate
                  return true;
                }

                return false;
              });
            }

            return {
              id: roommate._id,
              text: `${roommate.properties?.firstName || '-'} ${roommate.properties?.lastName || '-'}`,
              isDisabled: alreadySelected || !hasCompatibleProperties,
            };
          });
      };
    },
  },
});
